import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {FilterService} from './filter.service';
import {DOCUMENT} from '@angular/common';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {Uuid} from '../interface/response/uuid';
import {first, take} from 'rxjs/operators';
import {User} from '../interface/user';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {ListResponse} from '../interface/response/list-response';
import {CrmUserPermissions} from "../interface/crm-user-permissions";
import {Partner} from "../interface/partner";
import {Device} from "../interface/device";
import {License} from "../interface/license";
import {co} from "@fullcalendar/core/internal-common";

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly userListSubject: ReplaySubject<CountedAndPaginated<User>> = new ReplaySubject<CountedAndPaginated<User>>(1);
  private readonly employeesListSubject: ReplaySubject<CountedAndPaginated<User>> = new ReplaySubject<CountedAndPaginated<User>>(1);
  private readonly userNotCountedListSubject: ReplaySubject<ListResponse<User>> = new ReplaySubject<ListResponse<User>>(1);
  private readonly crmUserPermissionsListSubject: ReplaySubject<CountedAndPaginated<CrmUserPermissions>> = new ReplaySubject<CountedAndPaginated<CrmUserPermissions>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  public sendForm(formData: object, userId?: string): Observable<Uuid> {
    if (userId) {
      return this.httpClient.put(`/rest/sso/api/user/${userId}`, formData)
        .pipe(
          take(1)
        ) as Observable<Uuid>;
    } else {
      return this.httpClient.post('/rest/sso/api/user', formData)
        .pipe(
          take(1)
        ) as Observable<Uuid>;
    }
  }

  public sendLoginLock(): Observable<any> {
    return this.httpClient.get('/rest/sso/api/userLoginLockMessage', {});
  }

  public get(userId: string): Observable<User> {
    return this.httpClient.get(`/rest/sso/api/user/${userId}`)
      .pipe(first()) as Observable<User>;
  }

  public getDobryAgentUsers(userIds: string[]): Observable<ListResponse<User>> {
    return this.httpClient.post<ListResponse<User>>(
      '/rest/sso/api/dobry-agent/users',
      {userIds}
    ).pipe(first());
  }

  async getUsersSequentially(userIds: string[]): Promise<User[]> {
    const users: User[] = [];
    for (const userId of userIds) {
      try {
        const user = await this.get(userId).toPromise();
        users.push(user);
      } catch (error) {
        console.error(`Error fetching data for userId ${userId}:`, error);
      }
    }
    return users;
  }

  public getRemindPassword(userId: string): Observable<any> {
    return this.httpClient.get(`/rest/sso/api/user/${userId}/remind-pasword`)
      .pipe(first()) as Observable<any>;
  }


  public loadList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/sso/api/user?page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<User>) => {
        this.userListSubject.next(result);
      });
  }

  public loadEmployeesList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/sso/api/employees?page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<User>) => {
        this.userListSubject.next(result);
      });
  }

  public loadNotCountedList(page: number, pageSize: number, filters: object, observable?: Subject<ListResponse<User>>): void {
    this.httpClient
      .get(`/rest/sso/api/user?disableCount=1&page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: ListResponse<User>) => {
        if (observable) {
          observable.next(result);
        } else {
          this.userNotCountedListSubject.next(result);
        }
      });
  }

  public loadPermissions306List(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/crm/user?page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<CrmUserPermissions>) => {
        this.crmUserPermissionsListSubject.next(result);
      });
  }

  public getUserListSubject(): ReplaySubject<CountedAndPaginated<User>> {
    return this.userListSubject;
  }
  public getEmployeesListSubject(): ReplaySubject<CountedAndPaginated<User>> {
    return this.employeesListSubject;
  }

  public getUserNotCountedListSubject(): ReplaySubject<ListResponse<User>> {
    return this.userNotCountedListSubject;
  }

  public downloadReport(filters: object): void {
    this.document.location.href = `/rest/sso/api/user/method/report?` + this.filterService.parseFilters(filters);
  }

  public downloadAccessReport(filters: object): void {
    this.document.location.href = `/rest/sso/api/user/access/method/report?` + this.filterService.parseFilters(filters);
  }
  public downloadEmployeesReport(filters: object): void {
    this.document.location.href = `/rest/sso/api/employees/method/report?` + this.filterService.parseFilters(filters);
  }

  public sendInvitation(userId: string): Observable<any> {
    return this.httpClient
      .post(`/rest/sso/api/user/${userId}/invite`, {})
      .pipe(first()) as Observable<any>;
  }

  updatePermissions(userId: string, formData: any): Observable<any> {
    return this.httpClient.put(`/rest/sso/api/user/${userId}/permissions`, formData)
      .pipe(first());
  }
  updatePermissionsApplications(userId: string, formData: any): Observable<any> {
    return this.httpClient.put(`/rest/sso/api/user/${userId}/applications`, formData)
      .pipe(first());
  }

  updateAvatar(userId: string, fileBase64: string): Observable<any> {
    return this.httpClient.put(`/rest/sso/api/user/${userId}/avatar`, {content: fileBase64})
      .pipe(first());
  }

  changePassword(formData: object): Observable<any> {
    return this.httpClient.post(`/rest/sso/api/changePassword`, formData)
      .pipe(first());
  }

  getUserPermissions(uuid: string): Observable<any> {
    return this.httpClient.get(`/rest/sso/api/user/${uuid}/permissions`)
      .pipe(first());
  }

  updateProfile(rawValue: object): Observable<any> {
    return this.httpClient.put(`/rest/sso/api/profile`, rawValue)
      .pipe(first());
  }

  updateProfileAvatar(fileBase64: string): Observable<any> {
    return this.httpClient.put(`/rest/sso/api/profileAvatar`, {content: fileBase64})
      .pipe(first());
  }
  updateReportPermissions(userId: string, formData: any): Observable<any> {
    return this.httpClient.put(`/rest/sso/api/user/${userId}/reportPermissions`, formData)
      .pipe(first());
  }
  updateApplicationPermissions(userId: string, formData: any): Observable<any> {
    return this.httpClient.put(`/rest/sso/api/user/${userId}/applicationPermissions`, {
      applicationPermissions: formData
    }).pipe(first());
  }
  public getCrmUserPermissionsListSubject(): ReplaySubject<CountedAndPaginated<CrmUserPermissions>> {
    return this.crmUserPermissionsListSubject;
  }
  public sendCrmUserPermissions(formData: object): Observable<any> {
      return this.httpClient.put(`/rest/crm/user/permissions`, formData)
        .pipe(take(1));

  }
  public addPermissionTag(formData: { permissionTag: number }, uuid: string): Observable<any> {
    return this.httpClient.put(`/rest/sso/api/user/${uuid}/addPermissionTag`, formData)
      .pipe(take(1));

  }
  public importPermissionTag(formData: object): Observable<any> {
    return this.httpClient.put(`/rest/sso/api/importPermissionTagUser`, formData)
      .pipe(take(1));

  }

  public deletePermissionTag(formData: { permissionTag: number }, uuid: string): Observable<any> {
    return this.httpClient.put(`/rest/sso/api/user/${uuid}/deletePermissionTag`, formData)
      .pipe(take(1));

  }

  public getDevices(uuid: string): Observable<ListResponse<Device>> {
    return this.httpClient.get(`/rest/crm/user/${uuid}/devices`)
      .pipe(first()) as Observable<ListResponse<Device>>;
  }

  public getLicenses(uuid: string): Observable<ListResponse<License>> {
    return this.httpClient.get(`/rest/crm/user/${uuid}/licenses`)
      .pipe(first()) as Observable<ListResponse<License>>;
  }

  public loadEmployeesNotCountedList(page: number, pageSize: number, filters: object, observable?: Subject<ListResponse<User>>): void {
    this.httpClient
      .get(`/rest/sso/api/employees?disableCount=1&page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: ListResponse<User>) => {
        if (observable) {
          observable.next(result);
        } else {
          this.userNotCountedListSubject.next(result);
        }
      });
  }
}
